import React, { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
// mui
import { Divider, Grid, SelectChangeEvent } from '@mui/material';
// components
import StyledForm from 'components/styled-form';
import DatePickerController from 'controllers/date-picker-controller';
import SelectController from 'controllers/select-controller';
import TagsSelect from 'widgets/side-panel/sections/activity-feed/components/add-tags-button/components/tags-select';
// api
import { useSyncSelectedProject } from 'hooks/useSyncSelectedProject';
import { ReminderStatusEnum, SelectionStatusEnum } from 'redux-store/api/selections';
import { ITag } from 'redux-store/types';
// helpers
import {
  getOptionsSelectOptions,
  getSelectionPhaseItemsOptions,
  getSelectionPhaseOptions,
  getSelectionReminderProfileOptions,
  REMINDER_TRIGGER_OPTIONS,
  STATUS_OPTIONS,
} from '../helpers';
import { utcDateToZoned } from 'utils/postDateHelper';
// types
import { ISelectionFormValues, ISelectionStepProps } from '../../utils/types';
// styles
import { styles } from '../styles';

interface IProps extends ISelectionStepProps {
  projectId: number;
}

export const Reminder: FC<IProps> = ({
  handleSubmit,
  renderActionButtons,
  isSelectionApproved = false,
  projectPhaseItemId,
  projectPhaseId,
}) => {
  const { project: projectData } = useSyncSelectedProject();

  const form = useFormContext<ISelectionFormValues>();

  const [reminderStatus, phaseId, profiles, status, endDate, options] = form.watch([
    'reminderStatus',
    'projectPhaseId',
    'profileIds',
    'status',
    'endDate',
    'options',
  ]);

  const moreThanOneOption = options?.length > 1;

  const phasesOptions = useMemo(
    () => getSelectionPhaseOptions(projectData?.phases, projectPhaseId),
    [projectData?.phases, projectPhaseId]
  );

  const phaseItemsOptions = useMemo(
    () => getSelectionPhaseItemsOptions(projectData?.phases, phaseId, projectPhaseItemId),
    [projectData?.phases, phaseId, projectPhaseItemId]
  );

  const profilesOptions = useMemo(
    () => getSelectionReminderProfileOptions(projectData?.profiles),
    [projectData?.profiles]
  );

  const onPhaseChange = useCallback(({ target: { value } }: SelectChangeEvent<number>) => {
    if (typeof value === 'number') {
      form.setValue('projectPhaseId', value);
      form.setValue('projectPhaseItemId', null);
    }
  }, []);

  const onProfilesChange = useCallback((selectedValues: ITag[]) => {
    form.setValue('profileIds', selectedValues);
  }, []);

  const onProfilesDelete = useCallback((itemId: number) => {
    form.setValue(
      'profileIds',
      form.watch('profileIds').filter(({ id }) => id !== itemId)
    );
  }, []);

  const onSelectionOptionChange = (event: SelectChangeEvent<string>) => {
    const newOptionsValue = options.map((item) => {
      if (item.id === event.target.value) {
        return { ...item, isSelected: true };
      }

      return { ...item, isSelected: false };
    });

    form.setValue('options', newOptionsValue);
  };

  const selectedSelectionOption = options?.find(({ isSelected }) => isSelected);

  const optionsSelectOptions = getOptionsSelectOptions(options);

  const handleChangeEndDate = (e) => {
    form.setValue('endDate', utcDateToZoned(e));
  };

  return (
    <StyledForm onSubmit={form.handleSubmit(handleSubmit)}>
      <SelectController
        fullWidth
        isSelect
        withError
        name="status"
        label="Approval Status"
        placeholder="Select Status"
        options={STATUS_OPTIONS}
        disabled={isSelectionApproved}
      />

      {status === SelectionStatusEnum.APPROVED && moreThanOneOption && (
        <SelectController
          fullWidth
          isSelect
          label="Selection Option"
          placeholder="Select Option"
          options={optionsSelectOptions}
          value={selectedSelectionOption?.id ?? ''}
          onChange={onSelectionOptionChange}
          disabled={isSelectionApproved}
        />
      )}

      {status === SelectionStatusEnum.PENDING ? (
        <>
          <Divider />
          <Grid item xs sx={styles.reminderText}>
            Set up a reminder to specific users, based on a specific date or after a schedule item
            has been completed, that this selection is due.
          </Grid>
          <SelectController
            fullWidth
            isSelect
            withError
            label="How should this selection reminder be triggered?"
            name="reminderStatus"
            placeholder="Select Trigger"
            value={reminderStatus}
            options={REMINDER_TRIGGER_OPTIONS}
            renderValue={(val: ReminderStatusEnum | null) => (
              <p>
                {REMINDER_TRIGGER_OPTIONS.find(({ value: optionValue }) => optionValue === val)
                  ?.label ?? 'Select Trigger'}
              </p>
            )}
          />
        </>
      ) : null}

      {reminderStatus === ReminderStatusEnum.CUSTOM && status === SelectionStatusEnum.PENDING && (
        <DatePickerController
          required
          fullWidth
          icon
          disablePast
          withError
          name="endDate"
          label="Deadline Date"
          date={endDate ?? ''}
          additionalOnChange={handleChangeEndDate}
        />
      )}

      {reminderStatus === ReminderStatusEnum.SCHEDULE_PHASE &&
        status === SelectionStatusEnum.PENDING && (
          <Grid sx={{ display: 'flex', width: '100%', gap: '20px' }}>
            <SelectController
              fullWidth
              isSelect
              withError
              name="projectPhaseId"
              label="Schedule Phase"
              placeholder="Select"
              options={phasesOptions}
              onChange={onPhaseChange}
            />
            <SelectController
              fullWidth
              isSelect
              withError
              name="projectPhaseItemId"
              label="Schedule Item"
              placeholder="Select"
              options={phaseItemsOptions}
              disabled={!phaseItemsOptions.length}
            />
          </Grid>
        )}

      {reminderStatus &&
        reminderStatus !== ReminderStatusEnum.UNASSIGNED &&
        status === SelectionStatusEnum.PENDING && (
          <TagsSelect
            placeholder="Select who should be reminded"
            options={profilesOptions}
            value={profiles}
            handleChange={onProfilesChange}
            handleDelete={onProfilesDelete}
            type="profiles"
          />
        )}

      {renderActionButtons()}
    </StyledForm>
  );
};
